/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { CatalogItem, GroupedCatalogItem } from 'store/landing/types';
import { MODE_SELECTOR } from 'constants/app';
import { useLandingPageContext } from 'context/LandingPageContext';
import styles from './LPCatalogExtended.module.scss';

interface OwnProps {
  mode: MODE_SELECTOR;
  catalogItems?: GroupedCatalogItem;
  defaultSelected?: CatalogItem | null;
  titleColor?: string;
}

const LPCatalogExtended: FC<OwnProps> = ({ titleColor = '#315970', catalogItems, defaultSelected, mode }) => {
  if (!catalogItems) {
    return null;
  }

  const { t } = useTranslation();
  const { theme, germanNoteSign } = useLandingPageContext();
  const pricePerFormat = useMemo(
    () =>
      mode === MODE_SELECTOR.MODE_CANVAS_SELECTOR_PRICE_PER_FORMAT ||
      mode === MODE_SELECTOR.MODE_POSTER_SELECTOR_PRICE_PER_FORMAT,
    [mode],
  );
  const filteredDefaultSelected = useMemo(() => {
    let foundItem;
    let shape;
    let priceIndex;
    Object.keys(catalogItems).forEach((key) => {
      catalogItems[key].items.forEach((i, index) => {
        if (i.label === defaultSelected?.title) {
          shape = key;
          foundItem = i;
          priceIndex = index;
        }
      });
    });
    return { foundItem, shape, priceIndex };
  }, [defaultSelected, catalogItems]);
  const [selectedShape, setSelectedShape] = useState(filteredDefaultSelected.shape || Object.keys(catalogItems)[0]);
  const [selectedSizeIdx, setSelectedSizeIdx] = useState(filteredDefaultSelected.priceIndex || 0);
  const [selectedItem, setSelectedItem] = useState(
    filteredDefaultSelected.foundItem || catalogItems[selectedShape].items[selectedSizeIdx],
  );

  return (
    <div className={cn(styles.lpCatalog, styles[`theme-${theme}`])}>
      <div className={cn(styles.container)}>
        <div className={cn(styles.catalogBlockGroup, styles.catalogBlockGroupExtended)}>
          <div className={cn(styles.catalogExStep, styles.catalogExStep1)}>
            <div className={styles.catalogExHeading}>
              <span className={styles.catalogExStepBubble}>1</span>
              <span className={styles.catalogExStepText} style={{ color: titleColor }}>
                {t('txt_canvas_block_title1')}
              </span>
              <div className={styles.catalogExStepDecorationLine} />
            </div>
            <div id="CatalogProductFormat" className={styles.catalogExContainer}>
              <div className={styles.catalogExContainerContent}>
                {Object.keys(catalogItems).map((shapeLabel, index) => (
                  <>
                    <input
                      type="radio"
                      className={styles.catalogExRadio}
                      checked={shapeLabel === selectedShape}
                      name="catalog-shape"
                      id={`cshape${index}`}
                      data-value={`${catalogItems[shapeLabel].productImageUrl}`}
                      data-label={`${t(shapeLabel)}`}
                      onChange={() => setSelectedShape(shapeLabel)}
                    />
                    <label htmlFor={`cshape${index}`} className={styles.catalogBlock}>
                      <div className={styles.catalogBlockContent}>
                        <div className={styles.catalogBlockContentHeading}>
                          <div className={styles.catalogBlockContentRadioBox} />
                        </div>

                        <div
                          className={styles.catalogBlockImage}
                          style={{
                            backgroundImage: `url(${catalogItems[shapeLabel].productImageUrl})`,
                          }}
                        />
                        <div className={styles.catalogBlockImageTitle}>{t(shapeLabel)}</div>
                      </div>
                    </label>
                  </>
                ))}
              </div>
              <div className={styles.catalogExStepDelimiter} />
            </div>
          </div>

          <div className={cn(styles.catalogExStep, styles.catalogExStep2)}>
            <div className={cn(styles.catalogExHeading)}>
              <div className={cn(styles.catalogExHeadingWrapper)}>
                <span className={cn(styles.catalogExStepBubble)}>2</span>
                <span className={styles.catalogExStepText}>{t('txt_canvas_block_title2')}</span>
              </div>

              {!pricePerFormat && (
                <div id="CatalogProductPrice" className={cn(styles.catalogSpecPrice)}>
                  <div className={cn(styles.opPriceCrossed)} dangerouslySetInnerHTML={{ __html: selectedItem.price }} />
                  <div
                    className={cn(styles.opPriceRed)}
                    dangerouslySetInnerHTML={{ __html: selectedItem.price_discounted + germanNoteSign }}
                  />
                </div>
              )}
            </div>
            <div id="CatalogProductSize" className={cn(styles.catalogExContainer)}>
              <div className={cn(styles.catalogExContainerTable)} id="CatalogProductSizeContainer">
                {catalogItems[selectedShape].items.map(({ badgeText, label, price, price_discounted, url }, index) => {
                  return (
                    <>
                      <input
                        type="radio"
                        checked={index === selectedSizeIdx}
                        name="catalog-size"
                        id={`csize${index}`}
                        data-value={url}
                        data-label={label}
                        onChange={() => {
                          setSelectedSizeIdx(index);
                          setSelectedItem(catalogItems[selectedShape].items[index]);
                        }}
                      />
                      <label htmlFor={`csize${index}`} className={cn(styles.catalogBlockText)}>
                        <div>
                          {badgeText && <div className={cn(styles.catalogBlockBadgeSize)}>{badgeText}</div>}
                          {label && (
                            <span
                              className={cn(styles.label, {
                                [styles.labelSelected]: index === selectedSizeIdx,
                              })}
                            >
                              {label}
                            </span>
                          )}
                          {pricePerFormat && (
                            <div className={cn(styles.catalogExPrice)}>
                              {price !== price_discounted ? (
                                <>
                                  <div
                                    className={cn(styles.opPriceCrossed)}
                                    dangerouslySetInnerHTML={{ __html: price }}
                                  />
                                  <div
                                    className={cn(styles.opPriceRed)}
                                    dangerouslySetInnerHTML={{ __html: price_discounted + germanNoteSign }}
                                  />
                                </>
                              ) : (
                                <div className={styles.opPrice} dangerouslySetInnerHTML={{ __html: price }} />
                              )}
                            </div>
                          )}
                        </div>
                      </label>
                    </>
                  );
                })}
                <a href={selectedItem.url} className={cn(styles.catalogExLink)}>
                  <span>{t('txt_personalise')}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LPCatalogExtended;
